// Copyright (c) 2017 - present Arno Gourdol. All rights reserved.

// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the "Software"),
// to deal in the Software without restriction, including without limitation
// the rights to use, copy, modify, merge, publish, distribute, sublicense,
// and/or sell copies of the Software, and to permit persons to whom the
// Software is furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
// FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
// DEALINGS IN THE SOFTWARE.

// Original author GitHub: https://github.com/arnog/mathlive

// Usage
// This code is used to convert math to latex
// convertAsciiMathToLatex is used in TextEntryReactMathKeyboard.js file

const MODE_SHIFT_COMMANDS = [
    ['\\[', '\\]'],
    ['\\(', '\\)'],
    ['$$', '$$'],
    ['$', '$'], // Must be *after* $$..$$
    ['\\begin{math}', '\\end{math}'],
    ['\\begin{displaymath}', '\\end{displaymath}'],
    ['\\begin{equation}', '\\end{equation}'],
    ['\\begin{equation*}', '\\end{equation*}'],
];

const INLINE_SHORTCUTS = {
    '&': '\\&',
    '%': '\\%',
    '$': '\\$',

    // Primes
    "''": '^{\\doubleprime}',
    "'''": '^{\\prime\\prime\\prime}',
    "''''": '^{\\prime\\prime\\prime\\prime}',

    // Greek letters
    'alpha': '\\alpha',
    'delta': '\\delta',
    'Delta': '\\Delta',
    'pi': '\\pi',
    'Pi': '\\Pi',
    'theta': '\\theta',
    'Theta': '\\Theta',

    // Letter-like
    'ii': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\imaginaryI',
    },
    'jj': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\imaginaryJ',
    },
    'ee': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\exponentialE',
    },

    'nabla': '\\nabla',
    'grad': '\\nabla',
    'del': '\\partial',
    'deg': { after: 'digit+space', value: '\\degree' },

    'infty': '\\infty',

    '\u221E': '\\infty', // @TODO: doesn't work
    // '&infin;': '\\infty',
    // '&#8734;': '\\infty',
    'oo': {
        after:
        'nothing+digit+frac+surd+binop+relop+punct+array+openfence+closefence+space',
        value: '\\infty',
    },

    // Big operators
    '∑': '\\sum',
    'sum': '\\sum_{#?}^{#?}',
    'int': '\\int_{#?}^{#?}',
    'prod': '\\prod_{#?}^{#?}',
    'sqrt': '\\sqrt{#?}',
    // '∫':                    '\\int',             // There's a alt-B command for this
    '∆': '\\differentialD', // @TODO: is \\diffD most common?
    '∂': '\\differentialD',

    // Functions
    'arcsin': '\\arcsin',
    'arccos': '\\arccos',
    'arctan': '\\arctan',
    'arcsec': '\\arcsec',
    'arccsc': '\\arccsc',

    'arsinh': '\\arsinh',
    'arcosh': '\\arcosh',
    'artanh': '\\artanh',
    'arcsech': '\\arcsech',
    'arccsch': '\\arccsch',
    'arg': '\\arg',
    'ch': '\\ch',
    'cosec': '\\cosec',
    'cosh': '\\cosh',
    'cot': '\\cot',
    'cotg': '\\cotg',
    'coth': '\\coth',
    'csc': '\\csc',
    'ctg': '\\ctg',
    'cth': '\\cth',
    'sec': '\\sec',
    'sinh': '\\sinh',
    'sh': '\\sh',
    'tanh': '\\tanh',
    'tg': '\\tg',
    'th': '\\th',

    'sin': '\\sin',
    'cos': '\\cos',
    'tan': '\\tan',

    'lg': '\\lg',
    'lb': '\\lb',
    'log': '\\log',
    'ln': '\\ln',
    'exp': '\\exp',
    'lim': '\\lim_{#?}',

    // Differentials
    // According to ISO31/XI (ISO 80000-2), differentials should be upright
    'dx': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\differentialD x',
    },
    'dy': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\differentialD y',
    },
    'dt': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\differentialD t',
    },

    // Logic
    'AA': '\\forall',
    'EE': '\\exists',
    '!EE': '\\nexists',
    '&&': '\\land',
    // The shortcut for the greek letter "xi" is interfering with "x in"
    'xin': {
        after: 'nothing+text+relop+punct+openfence+space',
        value: 'x \\in',
    },
    // The shortcut for `\int` is interfering with `\sin x`
    'sint': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\sin t',
    },
    'in': {
        after: 'nothing+letter+closefence',
        value: '\\in',
    },
    '!in': '\\notin',

    // Sets
    'NN': '\\mathbb{N}', // Natural numbers
    'ZZ': '\\Z', // Integers
    'QQ': '\\Q', // Rational numbers
    'RR': '\\R', // Real numbers
    'CC': '\\C', // Complex numbers

    // Operators
    'xx': '\\times',
    '+-': '\\pm',

    // Relational operators
    '≠': '\\ne',
    '!=': '\\ne',
    '\u2265': '\\ge',
    '>=': '\\ge',
    '\u2264': '\\le',
    '<=': '\\le',
    '<<': '\\ll',
    '>>': '\\gg',
    '~~': '\\approx',

    // More operators
    '≈': '\\approx',
    '?=': '\\questeq',
    '÷': '\\div',
    '¬': '\\neg',
    ':=': '\\coloneq',
    '::': '\\Colon',

    // Fences
    '(:': '\\langle',
    ':)': '\\rangle',

    // More Greek letters
    'beta': '\\beta',
    'chi': '\\chi',
    'epsilon': '\\epsilon',
    'varepsilon': '\\varepsilon',
    'eta': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\eta',
    },
    'gamma': '\\gamma',
    'Gamma': '\\Gamma',
    'iota': '\\iota',
    'kappa': '\\kappa',
    'lambda': '\\lambda',
    'Lambda': '\\Lambda',
    'mu': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\mu',
    },
    'nu': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\nu',
    },
    'µ': '\\mu', // @TODO: or micro?
    'phi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\phi',
    },
    'Phi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\Phi',
    },
    'varphi': '\\varphi',
    'psi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\psi',
    },
    'Psi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\Psi',
    },
    'rho': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\rho',
    },
    'sigma': '\\sigma',
    'Sigma': '\\Sigma',
    'tau': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\tau',
    },
    'vartheta': '\\vartheta',
    'upsilon': '\\upsilon',
    'xi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space',
        value: '\\xi',
    },
    'Xi': {
        after:
        'nothing+digit+function+frac+surd+binop+relop+punct+array+openfence+closefence+space+text',
        value: '\\Xi',
    },
    'zeta': '\\zeta',
    'omega': '\\omega',
    'Omega': '\\Omega',
    'Ω': '\\omega', // @TODO: or ohm?

    // More Logic
    'forall': '\\forall',
    'exists': '\\exists',
    '!exists': '\\nexists',
    ':.': '\\therefore',
    // MORE FUNCTIONS
    // 'arg': '\\arg',
    'liminf': '\\liminf_{#?}',
    'limsup': '\\limsup_{#?}',
    'argmin': '\\operatorname*{arg~min}_{#?}',
    'argmax': '\\operatorname*{arg~max}_{#?}',
    'det': '\\det',
    'mod': '\\bmod{#?}',
    '(mod': '\\pmod{#?}',
    'max': '\\max',
    'min': '\\min',

    'erf': '\\operatorname{erf}',
    'erfc': '\\operatorname{erfc}',
    'bessel': '\\operatorname{bessel}',
    'mean': '\\operatorname{mean}',
    'median': '\\operatorname{median}',

    'fft': '\\operatorname{fft}',

    'lcm': '\\operatorname{lcm}',

    'gcd': '\\operatorname{gcd}',

    'randomReal': '\\operatorname{randomReal}',
    'randomInteger': '\\operatorname{randomInteger}',
    'Re': '\\operatorname{Re}',

    'Im': '\\operatorname{Im}',

    // UNITS
    'mm': {
        after: 'nothing+digit+operator',
        value: '\\operatorname{mm}', // Millimeter
    },
    'cm': {
        after: 'nothing+digit+operator',
        value: '\\operatorname{cm}', // Centimeter
    },
    'km': {
        after: 'nothing+digit+operator',
        value: '\\operatorname{km}', // Kilometer
    },
    'kg': {
        after: 'nothing+digit+operator',
        value: '\\operatorname{kg}', // Kilogram
    },

    // '||':                   '\\lor',
    '...': '\\ldots', // In general, use \ldots
    '+...': '+\\cdots', // ... but use \cdots after + ...
    '-...': '-\\cdots', // ... - and ...
    '->...': '\\to\\cdots', // ->
    '-->...': '\\longrightarrow\\cdots',

    '->': '\\to',
    '|->': '\\mapsto',
    '-->': '\\longrightarrow',
    //    '<-':                   '\\leftarrow',
    '<--': '\\longleftarrow',
    '=>': '\\Rightarrow',
    '==>': '\\Longrightarrow',
    // '<=': '\\Leftarrow',     // CONFLICTS WITH LESS THAN OR EQUAL
    '<=>': '\\Leftrightarrow',
    '<->': '\\leftrightarrow',

    '(.)': '\\odot',
    '(+)': '\\oplus',
    '(/)': '\\oslash',
    '(*)': '\\otimes',
    '(-)': '\\ominus',
    // '(-)':                  '\\circleddash',

    '||': '\\Vert',

    //
    // ASCIIIMath
    //
    // Binary operation symbols
    //
    '**': '\\star',
    '***': '\\ast',
    '//': '\\slash',
    '\\\\': '\\backslash',
    'setminus': '\\backslash',
    '|><': '\\ltimes',
    '><|': '\\rtimes',
    '|><|': '\\bowtie',
    '-:': '\\div',
    'divide': '\\div',
    '@': '\\circ',
    // 'o+': '\\oplus',
    // 'ox': '\\otimes',
    // 'o.': '\\odot',
    '^^': '\\wedge',
    '^^^': '\\bigwedge',
    'vv': '\\vee',
    'vvv': '\\bigvee',
    'nn': '\\cap',
    'nnn': '\\bigcap',
    'uu': '\\cup',
    'uuu': '\\bigcup',

    // Binary relation symbols
    '-=': '\\equiv',
    '~=': '\\cong',
    'lt': '<',
    'lt=': '\\leq',
    'gt': '>',
    'gt=': '\\geq',
    '-<': '\\prec',
    '-lt': '\\prec',
    '-<=': '\\preceq',
    // '>-':                   '\\succ',
    '>-=': '\\succeq',
    'prop': '\\propto',
    'diamond': '\\diamond',
    'square': '\\square',
    'iff': '\\iff',

    'sub': '\\subset',
    'sup': '\\supset',
    'sube': '\\subseteq',
    'supe': '\\supseteq',
    'uarr': '\\uparrow',
    'darr': '\\downarrow',
    'rarr': '\\rightarrow',
    'rArr': '\\Rightarrow',
    'larr': '\\leftarrow',
    'lArr': '\\Leftarrow',
    'harr': '\\leftrightarrow',
    'hArr': '\\Leftrightarrow',
    'aleph': '\\aleph',

    // Logic
    'and': '\\land',
    'or': '\\lor',
    'not': '\\neg',
    '_|_': '\\bot',
    'TT': '\\top',
    '|--': '\\vdash',
    '|==': '\\models',

    // Other functions
    '|__': '\\lfloor',
    '__|': '\\rfloor',

    '|~': '\\lceil',
    '~|': '\\rceil',

    // Arrows
    '>->': '\\rightarrowtail',
    '->>': '\\twoheadrightarrow', // \char"21A0
    '>->>': '\\twoheadrightarrowtail', // \char"2916

    //
    // Desmos Graphing Calculator
    //
    'frac': '\\frac{#?}{#?}',
    'cbrt': '\\sqrt[3]{#?}',
    'nthroot': '\\sqrt[#?]{#?}',
};

export function isBrowser() {
    return window && document;
}

export function supportRegexPropertyEscape() {
    if (!isBrowser()) return true;

    if (/firefox/i.test(navigator.userAgent)) {
        const m = navigator.userAgent.match(/firefox\/(\d+)/i);
        if (!m) return false;
        const version = parseInt(m[1], 10);
        return version >= 78; // https://www.mozilla.org/en-US/firefox/78.0/releasenotes/
    }
    if (/trident/i.test(navigator.userAgent)) return false;

    if (/edge/i.test(navigator.userAgent)) {
        const m = navigator.userAgent.match(/edg\/(\d+)/i);
        if (!m) return false;
        const version = parseInt(m[1], 10);
        return version >= 79;
    }

    return true;
}

let LETTER;

if (supportRegexPropertyEscape()) {
    LETTER = new RegExp('\\p{Letter}', 'u');
} else {
    LETTER =
      /[a-zA-ZаАбБвВгГдДеЕёЁжЖзЗиИйЙкКлЛмМнНоОпПрРсСтТуУфФхХцЦчЧшШщЩъЪыЫьЬэЭюЮяĄąĆćĘęŁłŃńÓóŚśŹźŻżàâäôéèëêïîçùûüÿæœÀÂÄÔÉÈËÊÏÎŸÇÙÛÜÆŒößÖẞìíòúÌÍÒÚáñÁÑ]/;
}

export function paddedShortcut(
    s,
    shortcuts
) {
    let result = getInlineShortcut(null, s, shortcuts);
    if (result) {
        result = result.replace('_{#?}', '');
        result = result.replace('^{#?}', '');
        result += ' ';
    } else result = s;

    return result;
}

export function trimModeShiftCommand(s) {
    const trimmedString = s.trim();

    for (const mode of MODE_SHIFT_COMMANDS) {
        if (trimmedString.startsWith(mode[0]) && trimmedString.endsWith(mode[1])) {
            return [
                true,
                trimmedString.substring(
                    mode[0].length,
                    trimmedString.length - mode[1].length
                ),
            ];
        }
    }

    return [false, s];
}

export function inferFormat(s) {
    let trimmedString = s.trim();

    // Assume LaTeX if a single char
    if (trimmedString.length <= 1) return ['latex', trimmedString];

    // This is not explicitly ASCIIMath. Try to infer if this is LaTex...
    const hasLatexModeShiftCommand = trimModeShiftCommand(trimmedString)[0];
    trimmedString = trimModeShiftCommand(trimmedString)[1];
    if (hasLatexModeShiftCommand) return ['latex', trimmedString];

    // The backtick is the default delimiter used by MathJAX for
    // ASCII Math
    if (trimmedString.startsWith('`') && trimmedString.endsWith('`')) {
        trimmedString = trimmedString.substring(1, trimmedString.length - 1);
        return ['ascii-math', trimmedString];
    }

    if (trimmedString.includes('\\')) {
        // If the string includes a '\' it's probably a LaTeX string
        // (that's not completely true, it could be a UnicodeMath string, since
        // UnicodeMath supports some LaTeX commands. However, we need to pick
        // one in order to correctly interpret {} (which are argument delimiters
        // in LaTeX, and are fences in UnicodeMath)
        return ['latex', trimmedString];
    }

    if (/\$.+\$/.test(trimmedString)) {
        // If there's a pair of $ (or possibly $$) signs, assume it's a string
        // such as `if $x<0$ then`, in which case it's LaTeX, but with some text
        // around it
        return ['latex', `\\text{${trimmedString}}`];
    }

    return [undefined, trimmedString];
}

export function parseMathString(
    s,
    options
) {
    let format = options?.format ? options.format : 'auto';
    let formattedString = s;
    if (format === 'auto') [format, formattedString] = inferFormat(s);

    if (format === 'ascii-math') {
        formattedString = formattedString.replace(/\u2061/gu, ''); // Remove function application
        formattedString = formattedString.replace(/\u3016/gu, '{'); // WHITE LENTICULAR BRACKET (grouping)
        formattedString = formattedString.replace(/\u3017/gu, '}'); // WHITE LENTICULAR BRACKET (grouping)

        formattedString = formattedString.replace(/([^\\])sinx/g, '$1\\sin x'); // Common typo
        formattedString = formattedString.replace(/([^\\])cosx/g, '$1\\cos x '); // Common typo
        formattedString = formattedString.replace(/\u2013/g, '-'); // EN-DASH, sometimes used as a minus sign

        return [
            'ascii-math',
            parseMathExpression(formattedString, { inlineShortcuts: options?.inlineShortcuts }),
        ];
    }

    return ['latex', formattedString];
}

export function parseMathExpression(
    s,
    options
) {
    const trimmedString = s.trim();
    if (!trimmedString) return '';

    const inlineShortcuts = options.inlineShortcuts ? options.inlineShortcuts : INLINE_SHORTCUTS;

    if (trimmedString.startsWith('^') || trimmedString.startsWith('_')) {
        // Superscript and subscript
        const { match, rest } = parseMathArgument(trimmedString.slice(1), {
            inlineShortcuts,
            noWrap: true,
        });
        return `${trimmedString[0]}{${match}}${parseMathExpression(rest, options)}`;
    }

    let m = trimmedString.match(/^(sqrt|\u221A)(.*)/);
    if (m) {
        // Square root
        const { match, rest } = parseMathArgument(m[2], {
            inlineShortcuts,
            noWrap: true,
        });
        return `\\sqrt{${match || '\\placeholder{}'}}${parseMathExpression(
            rest,
            options
        )}`;
    }

    m = trimmedString.match(/^(\\cbrt|\u221B)(.*)/);
    if (m) {
        // Cube root
        const { match, rest } = parseMathArgument(m[2], {
            inlineShortcuts,
            noWrap: true,
        });
        return `\\sqrt[3]{${match || '\\placeholder{}'}}${parseMathExpression(
            rest,
            options
        )}`;
    }

    m = trimmedString.match(/^abs(.*)/);
    if (m) {
        // Absolute value
        const { match, rest } = parseMathArgument(m[1], {
            inlineShortcuts,
            noWrap: true,
        });
        return `\\left|${match || '\\placeholder{}'}\\right|${parseMathExpression(
            rest,
            options
        )}`;
    }

    m = trimmedString.match(/^["”“](.*?)["”“](.*)/);
    if (m) {
        // Quoted text
        return `\\text{${m[1]}}${parseMathExpression(m[2], options)}`;
    }

    m = trimmedString.match(/^([^a-zA-Z({[_^\\\s"]+)(.*)/);
    // A string of symbols...
    // Could be a binary or relational operator, etc...
    if (m) {
        return `${paddedShortcut(m[1], inlineShortcuts)}${parseMathExpression(
            m[2],
            options
        )}`;
    }

    if (/^([fgh])[^a-zA-Z]/.test(trimmedString)) {
        // This could be a function...
        const { rest, match } = parseMathArgument(trimmedString.slice(1), {
            inlineShortcuts,
            noWrap: true,
        });
        let result = '';
        if (trimmedString[1] === '(') result = `${trimmedString[0]}\\left(${match}\\right)`;
        else result = trimmedString[0] + match;
        return result + parseMathExpression(rest, options);
    }

    m = trimmedString.match(/^([a-zA-Z]+)(.*)/);
    if (m) {
        // Some alphabetical string...
        // Could be a function name (sin) or symbol name (alpha)
        return (
            paddedShortcut(m[1], inlineShortcuts) + parseMathExpression(m[2], options)
        );
    }

    const { match, rest } = parseMathArgument(trimmedString, {
        inlineShortcuts,
        noWrap: true,
    });
    if (match && rest[0] === '/') {
        // Fraction
        const m2 = parseMathArgument(rest.slice(1), {
            inlineShortcuts,
            noWrap: true,
        });
        if (m2.match) {
            return `\\frac{${match}}{${m2.match}}${parseMathExpression(
                m2.rest,
                options
            )}`;
        }
    } else if (match) {
        return trimmedString.startsWith('(')
            ? `\\left(${match}\\right)${parseMathExpression(rest, options)}`
            : match + parseMathExpression(rest, options);
    }

    m = trimmedString.match(/^(\s+)([^\s]*)$/);
    // Whitespace
    if (m) return ` ${parseMathExpression(m[2], options)}`;

    return trimmedString;
}

export function parseMathArgument(
    s,
    options
) {
    let match = '';
    const trimmedString = s.trim();
    let rest = trimmedString;
    let lFence = trimmedString.charAt(0);
    let rFence = { '(': ')', '{': '}', '[': ']' }[lFence];
    if (rFence) {
        // It's a fence
        let level = 1;
        let i = 1;
        while (i < s.length && level > 0) {
            if (s[i] === lFence) level++;
            if (s[i] === rFence) level--;
            i++;
        }

        if (level === 0) {
        // We've found the matching closing fence
            if (options.noWrap && lFence === '(') { match = parseMathExpression(trimmedString.substring(1, i - 1), options); } else {
                if (lFence === '{' && rFence === '}') {
                    lFence = '\\{';
                    rFence = '\\}';
                }

                match =
            `\\left${
                lFence
            }${parseMathExpression(s.substring(1, i - 1), options)
            }\\right${
                rFence}`;
            }

            rest = trimmedString.slice(Math.max(0, i));
        } else {
        // Unbalanced fence...
            match = trimmedString.substring(1, i);
            rest = '';
        }
    } else {
        let m = trimmedString.match(/^([a-zA-Z]+)/);
        if (m) {
        // It's a string of letter, maybe a shortcut
            let shortcut = getInlineShortcut(null, trimmedString, options.inlineShortcuts);
            if (shortcut) {
                shortcut = shortcut.replace('_{#?}', '');
                shortcut = shortcut.replace('^{#?}', '');
                return { match: shortcut, rest: trimmedString.slice(shortcut.length) };
            }
        }

        m = trimmedString.match(/^([a-zA-Z])/);
        if (m) {
        // It's a single letter
            return { match: m[1], rest: trimmedString.slice(1) };
        }

        m = trimmedString.match(/^(-)?\d+(\.\d*)?/);
        if (m) {
        // It's a number
            return { match: m[0], rest: trimmedString.slice(m[0].length) };
        }

        if (!/^\\(left|right)/.test(trimmedString)) {
        // It's a LaTeX command (but not a \left\right)
            m = trimmedString.match(/^(\\[a-zA-Z]+)/);
            if (m) {
                rest = trimmedString.slice(m[1].length);
                match = m[1];
            }
        }
    }

    return { match, rest };
}

export function validateShortcut(
    siblings,
    shortcut
) {
    if (!shortcut) return '';

    // If it's a simple shortcut (no conditional), it's always valid
    if (typeof shortcut === 'string') return shortcut;

    // If we have no context, we assume all the shortcuts are valid
    if (!siblings || shortcut.after === undefined) return shortcut.value;

    let nothing = false;
    let letter = false;
    let digit = false;
    let isFunction = false;
    let frac = false;
    let surd = false;
    let binop = false;
    let relop = false;
    let operator = false;
    let punct = false;
    let array = false;
    let openfence = false;
    let closefence = false;
    let text = false;
    let space = false;

    // Find first sibling left which is not a placeholder or subsup
    let sibling = siblings[0]; // sibling immediately left
    let index = 0;
    while (sibling?.type && /^(subsup|placeholder)$/.test(sibling.type)) {
        index += 1;
        sibling = siblings[index];
    }

    nothing = !sibling || sibling.type === 'first'; // Start of a group
    if (sibling) {
        text = sibling.mode === 'text';
        letter = !text && sibling.type === 'mord' && LETTER.test(sibling.value);
        digit = !text && sibling.type === 'mord' && /\d{1,20}$/.test(sibling.value);
        isFunction = !text && sibling.isFunction;
        frac = sibling.type === 'genfrac';
        surd = sibling.type === 'surd';
        binop = sibling.type === 'mbin';
        relop = sibling.type === 'mrel';
        operator =
        sibling.type === 'mop' ||
        sibling.type === 'operator' ||
        sibling.type === 'extensible-symbol';
        punct = sibling.type === 'mpunct' || sibling.type === 'minner';
        array = sibling.type === 'array';
        openfence = sibling.type === 'mopen';
        closefence = sibling.type === 'mclose' || sibling.type === 'leftright';
        space = sibling.type === 'space';
    }

    // If this is a conditional shortcut, consider the conditions now
    if (
        (shortcut.after.includes('nothing') && nothing) ||
      (shortcut.after.includes('letter') && letter) ||
      (shortcut.after.includes('digit') && digit) ||
      (shortcut.after.includes('function') && isFunction) ||
      (shortcut.after.includes('frac') && frac) ||
      (shortcut.after.includes('surd') && surd) ||
      (shortcut.after.includes('binop') && binop) ||
      (shortcut.after.includes('relop') && relop) ||
      (shortcut.after.includes('operator') && operator) ||
      (shortcut.after.includes('punct') && punct) ||
      (shortcut.after.includes('array') && array) ||
      (shortcut.after.includes('openfence') && openfence) ||
      (shortcut.after.includes('closefence') && closefence) ||
      (shortcut.after.includes('text') && text) ||
      (shortcut.after.includes('space') && space)
    ) {
        return shortcut.value;
    }

    return '';
}

export function getInlineShortcut(
    context,
    s,
    shortcuts
) {
    if (!shortcuts) return '';
    return validateShortcut(context, shortcuts[s]);
}

export function convertAsciiMathToLatex(ascii) {
    return parseMathString(ascii, { format: 'ascii-math' })[1];
}
